/**
 * Little Giants — Order Tracker Widget v4 (Fabric theme build)
 * =========================================
 * Colors are pulled from the theme's CSS custom-property palette
 * (--color-foreground / --color-background / --color-primary-button-*)
 * so the widget matches whatever palette is set in the theme editor.
 * Shopify Assets: upload this file as assets/tracker.js in your theme.
 *
 * Liquid template:
 *   <div id="lg-order-tracker" data-wa="+923XXXXXXXXX" data-hub="https://hub.littlegiants.pk"></div>
 *   <script src="{{ 'tracker.js' | asset_url }}" data-hub="https://hub.littlegiants.pk"></script>
 */
(function () {
  "use strict";

  // ── Config ─────────────────────────────────────────────────────────────────
  const _script =
    document.currentScript ||
    document.querySelector('script[data-hub]') ||
    document.querySelector('script[src*="tracker.js"]');

  function _hubBase() {
    if (_script && _script.dataset.hub) return _script.dataset.hub.replace(/\/$/, "");
    if (_script && _script.src) { try { return new URL(_script.src).origin; } catch (_) {} }
    return window.location.origin;
  }

  const HUB_BASE = _hubBase();
  const ROOT  = document.getElementById("lg-order-tracker") || _injectRoot();
  const WA    = (ROOT && ROOT.dataset.wa)    || (_script && _script.dataset.wa)    || "";
  const BRAND = (ROOT && ROOT.dataset.brand) || (_script && _script.dataset.brand) || "Little Giants";
  let HERITAGE_ACCENT = "";
  try {
    HERITAGE_ACCENT = getComputedStyle(document.documentElement)
      .getPropertyValue("--color-primary-button-background")
      .trim();
  } catch (_) {}
  const COLOR = (ROOT && ROOT.dataset.color) || (_script && _script.dataset.color) || HERITAGE_ACCENT || "#B08F4A";

  // ── Entry ─────────────────────────────────────────────────────────────────
  _injectStyles();
  const params  = new URLSearchParams(window.location.search);
  const cnParam = params.get("cn") || params.get("nums") || "";

  if (cnParam) {
    _renderLoading();
    _fetch(`?cn=${encodeURIComponent(cnParam)}`);
  } else {
    _renderSearch();
  }

  // ── Fetch ─────────────────────────────────────────────────────────────────
  function _fetch(qs) {
    fetch(`${HUB_BASE}/api/public/track${qs}`)
      .then(function (r) { return r.json(); })
      .then(function (d) { d.ok ? _renderResult(d) : _renderError(d.error || "Order not found."); })
      .catch(function ()  { _renderError("Could not connect. Please try again."); });
  }

  // ── Search form ───────────────────────────────────────────────────────────
  function _renderSearch() {
    ROOT.innerHTML = `
      <div class="lgt-wrap">
        <div class="lgt-hero-search">
          <span class="lgt-brand-label">${_esc(BRAND)}</span>
          <h1 class="lgt-page-title">Track &amp; Trace</h1>
          <p class="lgt-search-hint">Enter your <strong>courier number</strong> from your dispatch SMS (e.g. ID7533031356)</p>
          <div class="lgt-search-row">
            <input id="lgt-input" class="lgt-input" type="text" placeholder="e.g. ID7533031356" autocomplete="off" />
            <button id="lgt-btn" class="lgt-btn">Track</button>
          </div>
        </div>
        ${WA ? _renderHelp() : ""}
      </div>`;
    document.getElementById("lgt-btn").addEventListener("click", _handleSearch);
    document.getElementById("lgt-input").addEventListener("keydown", function (e) { if (e.key === "Enter") _handleSearch(); });
  }

  function _handleSearch() {
    const val = (document.getElementById("lgt-input").value || "").trim();
    if (!val) return;
    _renderLoading();
    _fetch(`?cn=${encodeURIComponent(val)}`);
  }

  // ── Loading ───────────────────────────────────────────────────────────────
  function _renderLoading() {
    ROOT.innerHTML = `
      <div class="lgt-wrap">
        <div class="lgt-loading">
          <div class="lgt-spinner"></div>
          <p>Looking up your order…</p>
        </div>
      </div>`;
  }

  // ── Error ────────────────────────────────────────────────────────────────
  function _renderError(msg) {
    ROOT.innerHTML = `
      <div class="lgt-wrap">
        <div class="lgt-hero-search">
          <span class="lgt-brand-label">${_esc(BRAND)}</span>
          <h1 class="lgt-page-title">Track &amp; Trace</h1>
          <div class="lgt-error-notice">⚠️ ${_esc(msg)}</div>
          <div class="lgt-search-row">
            <input id="lgt-input" class="lgt-input" type="text" placeholder="e.g. ID7533031356" autocomplete="off" />
            <button id="lgt-btn" class="lgt-btn">Try Again</button>
          </div>
        </div>
        ${WA ? _renderHelp() : ""}
      </div>`;
    document.getElementById("lgt-btn").addEventListener("click", _handleSearch);
    document.getElementById("lgt-input").addEventListener("keydown", function (e) { if (e.key === "Enter") _handleSearch(); });
  }

  // ── Full result ───────────────────────────────────────────────────────────
  function _renderResult(data) {
    const order  = data.order;
    const steps  = data.steps      || [];
    const events = data.events     || [];
    const items  = data.line_items || [];

    ROOT.innerHTML = `
      <div class="lgt-wrap">

        ${_renderHero(order)}

        <div class="lgt-body">

          <div class="lgt-main">
            ${_renderStepper(steps, order)}
            ${events.length ? _renderTimeline(events) : _renderNoEvents(order)}
          </div>

          <div class="lgt-sidebar">
            ${_renderShipmentDetails(order)}
            ${order.city ? _renderMapCard(order.city) : ""}
            ${_renderReview()}
            ${_renderHelp()}
          </div>

        </div>

        <p class="lgt-footer-note"><a href="?" class="lgt-link">← Track another order</a></p>
      </div>`;

    // Initialise the map after DOM is ready
    if (order.city) {
      _initMap(order.city);
    }
  }

  // ── Status hero ───────────────────────────────────────────────────────────
  function _renderHero(order) {
    const statusMsg = {
      "CREATED":          "We've received your order.",
      "CONFIRMED":        "Your order is confirmed.",
      "BOOKED":           "Handed over to courier.",
      "DISPATCHED":       "Your order is on its way!",
      "OUT_FOR_DELIVERY": "Out for delivery today!",
      "DELIVERY_FAILED":  "Our courier attempted delivery but couldn't complete it. The package is being returned to us — we'll reach out shortly.",
      "DELIVERED":        "Your order has been delivered.",
      "RETURNED":         "This order has been returned.",
      "CANCELLED":        "This order was cancelled.",
      "REJECTED":         "This order was cancelled.",
    }[order.state] || order.state_label;

    const badgeCls = order.state === "DELIVERED"        ? "lgt-hero-badge-green"
                   : (order.state === "OUT_FOR_DELIVERY" || order.state === "DELIVERY_FAILED") ? "lgt-hero-badge-amber"
                   : (order.state === "RETURNED" || order.state === "CANCELLED" || order.state === "REJECTED") ? "lgt-hero-badge-grey"
                   : "lgt-hero-badge-blue";

    return `
      <div class="lgt-hero">
        <div class="lgt-hero-left">
          <span class="lgt-hero-badge ${badgeCls}">${_esc(order.state_label)}</span>
          <h2 class="lgt-hero-msg">${_esc(statusMsg)}</h2>
          <p class="lgt-hero-meta">
            Order ${_esc(order.name)}
            ${order.cn ? ` &bull; CN: <span class="lgt-hero-cn">${_esc(order.cn)}</span>` : ""}
            ${order.city ? ` &bull; 📍 ${_esc(order.city)}` : ""}
          </p>
        </div>
        <div class="lgt-hero-right">
          <span class="lgt-brand-label">${_esc(BRAND)}</span>
          <p class="lgt-hero-tracktrace">Track &amp; Trace</p>
          <div class="lgt-carrier-pill">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="1" y="3" width="15" height="13"/><polygon points="16 8 20 8 23 11 23 16 16 16 16 8"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg>
            Leopards Courier
          </div>
        </div>
      </div>`;
  }

  // ── Progress stepper — Option D segmented bar ────────────────────────
  function _renderStepper(steps, order) {
    if (order.is_terminal) {
      const icon  = order.state === "DELIVERED" ? "✅" : order.state === "RETURNED" ? "↩️" : "❌";
      return `<div class="lgt-card lgt-terminal"><span class="lgt-terminal-icon">${icon}</span><p class="lgt-terminal-label">${_esc(order.state_label)}</p></div>`;
    }

    const visible = steps.filter(function(s) { return !s.hidden; });
    const activeIdx = visible.findIndex(function(s) { return s.active || s.failed; });
    const stepNum   = activeIdx !== -1 ? activeIdx + 1 : visible.filter(function(s) { return s.done; }).length;

    const segs = visible.map(function(s) {
      var cls = s.done ? "lgt-seg-done" : s.active ? "lgt-seg-active" : s.failed ? "lgt-seg-failed" : "";
      return `<div class="lgt-seg ${cls}"></div>`;
    }).join("");

    const labels = visible.map(function(s) {
      var cls = s.done ? "lgt-seg-lbl-done" : (s.active || s.failed) ? "lgt-seg-lbl-active" : "";
      return `<span class="lgt-seg-lbl ${cls}">${_esc(s.failed ? "Attempted" : s.label)}</span>`;
    }).join("");

    return `
      <div class="lgt-card lgt-stepper-card">
        <div class="lgt-seg-bar">${segs}</div>
        <div class="lgt-seg-labels">${labels}</div>
        <p class="lgt-seg-status">Step ${stepNum} of ${visible.length} &mdash; <strong>${_esc(order.state_label)}</strong></p>
      </div>`;
  }

  // ── Tracking timeline ──────────────────────────────────────────────────
  function _renderTimeline(events) {
    const rows = events.slice(0, 12).map(function (ev, i) {
      return `
        <div class="lgt-event ${i === 0 ? "lgt-event-latest" : ""}">
          <div class="lgt-event-dot-wrap">
            <div class="lgt-event-dot ${i === 0 ? "lgt-event-dot-active" : ""}"></div>
            ${i < events.slice(0, 12).length - 1 ? '<div class="lgt-event-line"></div>' : ""}
          </div>
          <div class="lgt-event-body">
            <p class="lgt-event-status">${_esc(ev.status)}</p>
            ${ev.timestamp ? `<p class="lgt-event-time">${_fmtDate(ev.timestamp)}</p>` : ""}
          </div>
        </div>`;
    }).join("");

    return `
      <div class="lgt-card">
        <p class="lgt-section-label">Tracking History</p>
        <div class="lgt-timeline">${rows}</div>
      </div>`;
  }

  function _renderNoEvents(order) {
    var msg = "Tracking history will appear here once the courier scans your shipment.";
    if (order && order.state === "BOOKED") {
      msg = "Your shipment is booked and awaiting courier pickup — tracking will appear once scanned.";
    } else if (order && order.state === "DISPATCHED") {
      msg = "Your order is on its way. Courier scan events will appear here shortly — check back soon.";
    }
    return `
      <div class="lgt-card lgt-no-events">
        <p class="lgt-section-label">Tracking History</p>
        <p class="lgt-no-events-msg">${_esc(msg)}</p>
      </div>`;
  }

  // ── Shipment details ──────────────────────────────────────────────────
  function _renderShipmentDetails(order) {
    const invoiceUrl = order.cn ? `${HUB_BASE}/api/public/invoice?cn=${encodeURIComponent(order.cn)}` : null;

    return `
      <div class="lgt-card">
        <p class="lgt-section-label">Shipment Details</p>
        <div class="lgt-detail-row">
          <span class="lgt-detail-key">Carrier</span>
          <span class="lgt-detail-val">Leopards Courier</span>
        </div>
        ${order.cn ? `
        <div class="lgt-detail-row">
          <span class="lgt-detail-key">Tracking No.</span>
          <span class="lgt-detail-val lgt-cn-val">
            <span id="lgt-cn-text">${_esc(order.cn)}</span>
            <button class="lgt-copy-btn" onclick="navigator.clipboard.writeText('${_esc(order.cn)}').then(function(){var b=document.querySelector('.lgt-copy-btn');b.textContent='Copied!';setTimeout(function(){b.textContent='Copy';},1500)})">Copy</button>
          </span>
        </div>` : ""}
        ${order.city ? `
        <div class="lgt-detail-row">
          <span class="lgt-detail-key">Destination</span>
          <span class="lgt-detail-val">${_esc(order.city)}</span>
        </div>` : ""}
        ${order.created_at ? `
        <div class="lgt-detail-row">
          <span class="lgt-detail-key">Order Date</span>
          <span class="lgt-detail-val">${_fmtDateOnly(order.created_at)}</span>
        </div>` : ""}
        ${invoiceUrl ? `
        <div class="lgt-invoice-row">
          <a class="lgt-invoice-btn" href="${invoiceUrl}" target="_blank" rel="noopener">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>
            View Invoice
          </a>
        </div>` : ""}
      </div>`;
  }

  // ── Map card ────────────────────────────────────────────────────────────
  function _renderMapCard(city) {
    return `
      <div class="lgt-card lgt-map-card">
        <p class="lgt-section-label">Delivery Area <span class="lgt-map-city-label">📍 ${_esc(city)}</span></p>
        <div id="lgt-map" class="lgt-map-container"></div>
      </div>`;
  }

  function _initMap(city) {
    _loadLeaflet(function () {
      var L = window.L;
      fetch(
        "https://nominatim.openstreetmap.org/search?format=json&limit=1&q=" +
        encodeURIComponent(city + ", Pakistan"),
        { headers: { "Accept": "application/json" } }
      )
        .then(function (r) { return r.json(); })
        .then(function (results) {
          var mapEl = document.getElementById("lgt-map");
          if (!mapEl || !results.length) return;

          var lat = parseFloat(results[0].lat);
          var lon = parseFloat(results[0].lon);

          // Destroy any previous instance (if re-rendered)
          if (mapEl._leaflet_id) { return; }

          var map = L.map("lgt-map", {
            zoomControl: false,
            scrollWheelZoom: false,
            dragging: false,
            doubleClickZoom: false,
            touchZoom: false,
            keyboard: false,
          }).setView([lat, lon], 11);

          L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
            attribution: "© OpenStreetMap",
            maxZoom: 18,
          }).addTo(map);

          // Delivery area circle
          L.circle([lat, lon], {
            radius: 4000,
            color: COLOR,
            weight: 2,
            fillColor: COLOR,
            fillOpacity: 0.15,
          }).addTo(map);

          // Pin
          var icon = L.divIcon({
            className: "",
            html: '<div style="width:12px;height:12px;background:' + COLOR + ';border:2px solid #fff;border-radius:50%;box-shadow:0 1px 4px rgba(0,0,0,.4)"></div>',
            iconSize: [12, 12],
            iconAnchor: [6, 6],
          });
          L.marker([lat, lon], { icon: icon }).addTo(map);
        })
        .catch(function () {
          // Geocoding failed — hide the map card gracefully
          var mapEl = document.getElementById("lgt-map");
          if (mapEl) mapEl.parentElement.style.display = "none";
        });
    });
  }

  function _loadLeaflet(callback) {
    if (window.L) { callback(); return; }

    // CSS
    if (!document.getElementById("leaflet-css")) {
      var link = document.createElement("link");
      link.id   = "leaflet-css";
      link.rel  = "stylesheet";
      link.href = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
      document.head.appendChild(link);
    }

    // JS
    var s = document.createElement("script");
    s.src    = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js";
    s.onload = callback;
    s.onerror = function () { console.warn("Leaflet failed to load"); };
    document.head.appendChild(s);
  }

  // ── Review card ───────────────────────────────────────────────────────────
  function _renderReview() {
    return `
      <div class="lgt-card lgt-review-card">
        <p class="lgt-section-label">Enjoying your purchase?</p>
        <p class="lgt-review-text">Your feedback means a lot — share your experience on Google.</p>
        <a class="lgt-review-btn" href="https://g.page/r/CSPb6HuoQG-GEAE/review" target="_blank" rel="noopener">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
          </svg>
          Leave a Review
        </a>
      </div>`;
  }

  // ── Need Help card ───────────────────────────────────────────────────────
  function _renderHelp() {
    if (!WA) return "";
    const num = WA.replace(/\D/g, "");
    const msg = encodeURIComponent("Hi! I need help with my order.");
    return `
      <div class="lgt-card lgt-help-card">
        <p class="lgt-section-label">Need Help?</p>
        <p class="lgt-help-text">Questions about your delivery? Reach out to us directly.</p>
        <a class="lgt-wa-btn" href="https://wa.me/${num}?text=${msg}" target="_blank" rel="noopener">
          <svg width="17" height="17" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/><path d="M12 0C5.373 0 0 5.373 0 12c0 2.117.553 4.104 1.523 5.83L.057 23.625a.75.75 0 0 0 .918.918l5.795-1.466A11.942 11.942 0 0 0 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.75a9.694 9.694 0 0 1-4.932-1.346l-.353-.21-3.668.927.944-3.578-.23-.367A9.694 9.694 0 0 1 2.25 12C2.25 6.615 6.615 2.25 12 2.25S21.75 6.615 21.75 12 17.385 21.75 12 21.75z"/></svg>
          Chat on WhatsApp
        </a>
      </div>`;
  }

  // ── Helpers ───────────────────────────────────────────────────────────
  function _injectRoot() {
    const el = document.createElement("div");
    el.id = "lg-order-tracker";
    document.body.appendChild(el);
    return el;
  }

  function _esc(str) {
    if (!str) return "";
    return String(str).replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;").replace(/"/g,"&quot;");
  }

  function _fmtDate(iso) {
    try {
      return new Date(iso).toLocaleString("en-PK", { day:"numeric", month:"short", year:"numeric", hour:"2-digit", minute:"2-digit", hour12:true, timeZone:"Asia/Karachi" });
    } catch (_) { return iso; }
  }

  function _fmtDateOnly(iso) {
    try {
      return new Date(iso).toLocaleDateString("en-PK", { day:"numeric", month:"long", year:"numeric", timeZone:"Asia/Karachi" });
    } catch (_) { return iso; }
  }

  // ── Styles ───────────────────────────────────────────────────────────
  function _injectStyles() {
    if (document.getElementById("lgt-styles")) return;
    const c = COLOR;

    const css = `
      #lg-order-tracker{display:block !important;width:100% !important}
      #lg-order-tracker *{box-sizing:border-box !important;margin:0 !important;padding:0 !important}

      /* ── Wrap ───────────────────────────────────────────────────── */
      #lg-order-tracker .lgt-wrap{
        font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif !important;
        max-width:1200px !important;margin:0 auto !important;
        padding:0 0 48px !important;color:var(--color-foreground) !important;
      }

      /* ── Brand label ────────────────────────────────────────────────── */
      #lg-order-tracker .lgt-brand-label{
        display:block !important;font-size:10px !important;font-weight:800 !important;
        letter-spacing:.16em !important;text-transform:uppercase !important;color:${c} !important;
        margin-bottom:6px !important;
      }

      /* ── Hero ───────────────────────────────────────────────────── */
      #lg-order-tracker .lgt-hero{
        background:rgb(var(--color-foreground-rgb)) !important;color:var(--color-background) !important;
        border-radius:16px !important;padding:28px 28px !important;
        display:flex !important;align-items:flex-start !important;
        justify-content:space-between !important;gap:16px !important;
        margin-bottom:20px !important;
      }
      #lg-order-tracker .lgt-hero .lgt-brand-label{color:${c} !important}
      #lg-order-tracker .lgt-hero-left{flex:1 !important;min-width:0 !important}
      #lg-order-tracker .lgt-hero-right{text-align:right !important;flex-shrink:0 !important}
      #lg-order-tracker .lgt-hero-badge{
        display:inline-block !important;font-size:10px !important;font-weight:800 !important;
        padding:4px 12px !important;border-radius:20px !important;
        text-transform:uppercase !important;letter-spacing:.08em !important;
        margin-bottom:10px !important;
      }
      #lg-order-tracker .lgt-hero-badge-green{background:#d1fae5 !important;color:#065f46 !important}
      #lg-order-tracker .lgt-hero-badge-blue{background:#dbeafe !important;color:#1e40af !important}
      #lg-order-tracker .lgt-hero-badge-amber{background:#fef3c7 !important;color:#92400e !important}
      #lg-order-tracker .lgt-hero-badge-grey{background:#e5e7eb !important;color:#374151 !important}
      #lg-order-tracker .lgt-hero-msg{
        font-size:22px !important;font-weight:800 !important;color:var(--color-background) !important;
        line-height:1.25 !important;margin-bottom:10px !important;display:block !important;
      }
      #lg-order-tracker .lgt-hero-meta{
        font-size:12px !important;color:rgb(var(--color-background-rgb) / .55) !important;
        line-height:1.5 !important;display:block !important;
      }
      #lg-order-tracker .lgt-hero-cn{font-family:monospace !important;color:rgb(var(--color-background-rgb) / .8) !important}
      #lg-order-tracker .lgt-hero-tracktrace{
        font-size:13px !important;font-weight:700 !important;color:rgb(var(--color-background-rgb) / .9) !important;
        margin-top:2px !important;margin-bottom:8px !important;display:block !important;
      }
      #lg-order-tracker .lgt-carrier-pill{
        display:inline-flex !important;align-items:center !important;gap:5px !important;
        background:rgb(var(--color-background-rgb) / .1) !important;
        border:1px solid rgb(var(--color-background-rgb) / .18) !important;
        border-radius:20px !important;padding:4px 10px !important;
        font-size:11px !important;font-weight:600 !important;color:rgb(var(--color-background-rgb) / .75) !important;
      }
      #lg-order-tracker .lgt-carrier-pill svg{opacity:.7 !important}

      /* ── Body grid ────────────────────────────────────────────────── */
      #lg-order-tracker .lgt-body{
        display:grid !important;
        grid-template-columns:1fr 340px !important;
        gap:16px !important;align-items:start !important;
      }

      /* ── Cards ───────────────────────────────────────────────────── */
      #lg-order-tracker .lgt-card{
        background:rgb(var(--color-foreground-rgb) / 0.04) !important;border:1px solid rgb(var(--color-foreground-rgb) / 0.1) !important;
        border-radius:14px !important;padding:20px !important;
        margin-bottom:14px !important;
      }
      #lg-order-tracker .lgt-card:last-child{margin-bottom:0 !important}
      #lg-order-tracker .lgt-section-label{
        font-size:10px !important;font-weight:800 !important;letter-spacing:.12em !important;
        text-transform:uppercase !important;color:rgb(var(--color-foreground-rgb) / 0.45) !important;
        margin-bottom:14px !important;display:block !important;
      }
      #lg-order-tracker .lgt-item-count{
        font-size:10px !important;color:rgb(var(--color-foreground-rgb) / 0.4) !important;font-weight:600 !important;
        text-transform:none !important;letter-spacing:0 !important;
      }

      /* ── Segmented progress bar (Option D) ───────────────────── */
      #lg-order-tracker .lgt-seg-bar{
        display:flex !important;gap:5px !important;margin-bottom:10px !important;
      }
      #lg-order-tracker .lgt-seg{
        flex:1 !important;height:6px !important;border-radius:3px !important;
        background:rgb(var(--color-foreground-rgb) / 0.12) !important;
      }
      #lg-order-tracker .lgt-seg-done{background:${c} !important}
      #lg-order-tracker .lgt-seg-active{
        background:${c} !important;opacity:0.45 !important;
        position:relative !important;overflow:hidden !important;
      }
      #lg-order-tracker .lgt-seg-active::after{
        content:"" !important;position:absolute !important;
        top:0 !important;left:-100% !important;width:60% !important;height:100% !important;
        background:rgba(255,255,255,0.55) !important;
        animation:lgt-shim 1.6s ease-in-out infinite !important;
      }
      @keyframes lgt-shim{0%{left:-80%}100%{left:130%}}
      #lg-order-tracker .lgt-seg-failed{background:#f59e0b !important;opacity:0.6 !important}
      #lg-order-tracker .lgt-seg-labels{
        display:flex !important;
      }
      #lg-order-tracker .lgt-seg-lbl{
        flex:1 !important;font-size:9.5px !important;font-weight:600 !important;
        text-align:center !important;color:rgb(var(--color-foreground-rgb) / 0.4) !important;line-height:1.3 !important;
        display:block !important;
      }
      #lg-order-tracker .lgt-seg-lbl-done{color:rgb(var(--color-foreground-rgb) / 0.65) !important}
      #lg-order-tracker .lgt-seg-lbl-active{color:var(--color-foreground) !important;font-weight:800 !important}
      #lg-order-tracker .lgt-seg-status{
        font-size:11px !important;color:rgb(var(--color-foreground-rgb) / 0.45) !important;
        margin-top:10px !important;display:block !important;
      }
      #lg-order-tracker .lgt-seg-status strong{color:var(--color-foreground) !important;font-weight:700 !important}

      /* ── Terminal ────────────────────────────────────────────────── */
      #lg-order-tracker .lgt-terminal{text-align:center !important;padding:32px !important}
      #lg-order-tracker .lgt-terminal-icon{font-size:40px !important;display:block !important;margin-bottom:12px !important}
      #lg-order-tracker .lgt-terminal-label{font-size:18px !important;font-weight:800 !important;color:var(--color-foreground) !important;display:block !important}

      /* ── Timeline ─────────────────────────────────────────────── */
      #lg-order-tracker .lgt-timeline{display:flex !important;flex-direction:column !important}
      #lg-order-tracker .lgt-event{display:flex !important;align-items:flex-start !important;gap:12px !important}
      #lg-order-tracker .lgt-event-dot-wrap{
        display:flex !important;flex-direction:column !important;align-items:center !important;flex-shrink:0 !important;
      }
      #lg-order-tracker .lgt-event-dot{
        width:10px !important;height:10px !important;border-radius:50% !important;
        background:rgb(var(--color-foreground-rgb) / 0.15) !important;flex-shrink:0 !important;margin-top:4px !important;
      }
      #lg-order-tracker .lgt-event-dot-active{background:${c} !important;box-shadow:0 0 0 3px ${c}22 !important}
      #lg-order-tracker .lgt-event-line{
        width:2px !important;flex:1 !important;background:rgb(var(--color-foreground-rgb) / 0.1) !important;
        min-height:24px !important;margin:4px 0 !important;
      }
      #lg-order-tracker .lgt-event-body{flex:1 !important;padding-bottom:16px !important}
      #lg-order-tracker .lgt-event:last-child .lgt-event-body{padding-bottom:0 !important}
      #lg-order-tracker .lgt-event-status{
        font-size:13px !important;font-weight:500 !important;color:var(--color-foreground) !important;
        line-height:1.4 !important;display:block !important;
      }
      #lg-order-tracker .lgt-event-latest .lgt-event-status{font-weight:700 !important;color:var(--color-foreground) !important}
      #lg-order-tracker .lgt-event-time{font-size:11px !important;color:rgb(var(--color-foreground-rgb) / 0.45) !important;margin-top:2px !important;display:block !important}

      #lg-order-tracker .lgt-no-events{text-align:center !important;padding:32px 20px !important}
      #lg-order-tracker .lgt-no-events-msg{font-size:13px !important;color:rgb(var(--color-foreground-rgb) / 0.45) !important;display:block !important}

      /* ── Shipment details ───────────────────────────────────────── */
      #lg-order-tracker .lgt-detail-row{
        display:flex !important;justify-content:space-between !important;align-items:center !important;
        padding:9px 0 !important;border-bottom:1px solid rgb(var(--color-foreground-rgb) / 0.08) !important;
      }
      #lg-order-tracker .lgt-detail-row:last-of-type{border-bottom:none !important;padding-bottom:0 !important}
      #lg-order-tracker .lgt-detail-key{font-size:11px !important;color:rgb(var(--color-foreground-rgb) / 0.45) !important;font-weight:600 !important;text-transform:uppercase !important;letter-spacing:.06em !important}
      #lg-order-tracker .lgt-detail-val{font-size:13px !important;font-weight:600 !important;color:var(--color-foreground) !important}
      #lg-order-tracker .lgt-cn-val{display:flex !important;align-items:center !important;gap:8px !important;font-family:monospace !important}
      #lg-order-tracker .lgt-copy-btn{
        font-size:10px !important;font-weight:700 !important;color:${c} !important;
        background:none !important;border:1px solid ${c} !important;border-radius:6px !important;
        padding:2px 8px !important;cursor:pointer !important;font-family:inherit !important;
      }

      /* ── Invoice button ───────────────────────────────────────── */
      #lg-order-tracker .lgt-invoice-row{
        padding-top:12px !important;margin-top:4px !important;
      }
      #lg-order-tracker .lgt-invoice-btn{
        display:flex !important;align-items:center !important;justify-content:center !important;
        gap:7px !important;width:100% !important;
        background:var(--color-background) !important;color:${c} !important;
        border:1.5px solid ${c} !important;border-radius:10px !important;
        text-decoration:none !important;
        padding:10px !important;font-size:13px !important;font-weight:700 !important;
        transition:background .2s, color .2s !important;
      }
      #lg-order-tracker .lgt-invoice-btn:hover{background:${c} !important;color:#fff !important}
      #lg-order-tracker .lgt-invoice-btn svg{flex-shrink:0 !important;transition:stroke .2s !important}

      /* ── Map card ────────────────────────────────────────────────── */
      #lg-order-tracker .lgt-map-card{padding:16px !important}
      #lg-order-tracker .lgt-map-card .lgt-section-label{margin-bottom:10px !important}
      #lg-order-tracker .lgt-map-city-label{
        font-size:10px !important;color:rgb(var(--color-foreground-rgb) / 0.55) !important;font-weight:600 !important;
        text-transform:none !important;letter-spacing:0 !important;margin-left:4px !important;
      }
      #lg-order-tracker .lgt-map-container{
        width:100% !important;height:180px !important;border-radius:10px !important;
        overflow:hidden !important;background:rgb(var(--color-foreground-rgb) / 0.08) !important;
      }
      /* Make Leaflet respect the container */
      #lg-order-tracker .lgt-map-container .leaflet-container{
        width:100% !important;height:180px !important;border-radius:10px !important;
      }

      /* ── Order items ─────────────────────────────────────────────── */
      #lg-order-tracker .lgt-order-items{display:flex !important;flex-direction:column !important;gap:12px !important}
      #lg-order-tracker .lgt-order-item{display:flex !important;align-items:center !important;gap:12px !important}
      #lg-order-tracker .lgt-item-img{
        width:56px !important;height:56px !important;object-fit:cover !important;
        border-radius:8px !important;flex-shrink:0 !important;background:rgb(var(--color-foreground-rgb) / 0.08) !important;display:block !important;
      }
      #lg-order-tracker .lgt-item-placeholder{
        display:flex !important;align-items:center !important;justify-content:center !important;
        font-size:22px !important;
      }
      #lg-order-tracker .lgt-item-info{flex:1 !important;min-width:0 !important}
      #lg-order-tracker .lgt-item-title{
        font-size:12px !important;font-weight:600 !important;color:var(--color-foreground) !important;
        display:block !important;line-height:1.4 !important;
      }
      #lg-order-tracker .lgt-item-qty{font-size:11px !important;color:rgb(var(--color-foreground-rgb) / 0.45) !important;margin-top:2px !important;display:block !important}

      /* ── Review card ─────────────────────────────────────────────── */
      #lg-order-tracker .lgt-review-card{max-width:360px !important;margin-left:auto !important;margin-right:auto !important}
      #lg-order-tracker .lgt-review-text{font-size:12px !important;color:rgb(var(--color-foreground-rgb) / 0.55) !important;line-height:1.55 !important;margin-bottom:14px !important;display:block !important}
      #lg-order-tracker .lgt-review-btn{
        display:flex !important;align-items:center !important;justify-content:center !important;
        gap:8px !important;width:100% !important;
        background:var(--color-background) !important;color:#3c4043 !important;
        border:1.5px solid #dadce0 !important;border-radius:10px !important;
        text-decoration:none !important;
        padding:10px !important;font-size:13px !important;font-weight:600 !important;
        transition:background .2s, box-shadow .2s !important;
      }
      #lg-order-tracker .lgt-review-btn:hover{background:rgb(var(--color-foreground-rgb) / 0.03) !important;box-shadow:0 1px 3px rgb(var(--color-foreground-rgb) / .12) !important}

      /* ── Help card ─────────────────────────────────────────────── */
      /* Desktop: wide enough for the help text in one line, still centred */
      #lg-order-tracker .lgt-help-card{max-width:360px !important;margin-left:auto !important;margin-right:auto !important}
      #lg-order-tracker .lgt-help-text{font-size:12px !important;color:rgb(var(--color-foreground-rgb) / 0.55) !important;line-height:1.55 !important;margin-bottom:14px !important;display:block !important}
      #lg-order-tracker .lgt-wa-btn{
        display:flex !important;align-items:center !important;justify-content:center !important;
        gap:8px !important;width:100% !important;
        background:#25D366 !important;color:#fff !important;
        text-decoration:none !important;border-radius:10px !important;
        padding:12px !important;font-size:13px !important;font-weight:700 !important;
        transition:opacity .2s !important;
      }
      #lg-order-tracker .lgt-wa-btn:hover{opacity:.88 !important;color:#fff !important}

      /* ── Search hero ────────────────────────────────────────────────── */
      #lg-order-tracker .lgt-hero-search{
        background:rgb(var(--color-foreground-rgb)) !important;border-radius:16px !important;
        padding:40px 32px !important;margin-bottom:20px !important;text-align:center !important;
      }
      #lg-order-tracker .lgt-hero-search .lgt-brand-label{color:${c} !important}
      #lg-order-tracker .lgt-page-title{
        font-size:28px !important;font-weight:800 !important;color:var(--color-background) !important;
        margin-bottom:10px !important;display:block !important;
      }
      #lg-order-tracker .lgt-search-hint{
        font-size:13px !important;color:rgb(var(--color-background-rgb) / .5) !important;
        margin-bottom:20px !important;display:block !important;line-height:1.55 !important;
      }
      #lg-order-tracker .lgt-search-hint strong{color:rgb(var(--color-background-rgb) / .8) !important}
      #lg-order-tracker .lgt-search-row{display:flex !important;gap:8px !important;max-width:480px !important;margin:0 auto !important}
      #lg-order-tracker .lgt-input{
        flex:1 !important;min-width:0 !important;border:2px solid rgb(var(--color-background-rgb) / .15) !important;
        border-radius:10px !important;padding:13px 16px !important;font-size:14px !important;
        color:var(--color-background) !important;background:rgb(var(--color-background-rgb) / .08) !important;
        outline:none !important;transition:border-color .2s !important;
        -webkit-appearance:none !important;
      }
      #lg-order-tracker .lgt-input::placeholder{color:rgb(var(--color-background-rgb) / .3) !important}
      #lg-order-tracker .lgt-input:focus{border-color:${c} !important;background:rgb(var(--color-background-rgb) / .12) !important}
      #lg-order-tracker .lgt-btn{
        background:${c} !important;color:var(--color-primary-button-text) !important;border:none !important;
        border-radius:10px !important;padding:13px 24px !important;
        font-size:14px !important;font-weight:700 !important;
        cursor:pointer !important;white-space:nowrap !important;
        transition:opacity .2s !important;flex-shrink:0 !important;line-height:1 !important;
      }
      #lg-order-tracker .lgt-btn:hover{opacity:.85 !important}
      #lg-order-tracker .lgt-error-notice{
        background:rgba(220,38,38,.15) !important;border:1px solid rgba(220,38,38,.3) !important;
        color:#fca5a5 !important;border-radius:8px !important;
        padding:10px 14px !important;font-size:13px !important;
        margin-bottom:16px !important;display:block !important;
      }

      /* ── Loading ───────────────────────────────────────────────────── */
      #lg-order-tracker .lgt-loading{text-align:center !important;padding:60px 0 !important;color:rgb(var(--color-foreground-rgb) / 0.45) !important}
      #lg-order-tracker .lgt-spinner{
        width:38px !important;height:38px !important;border:3px solid rgb(var(--color-foreground-rgb) / 0.1) !important;
        border-top-color:${c} !important;border-radius:50% !important;
        animation:lgt-spin .75s linear infinite !important;margin:0 auto 14px !important;
      }
      @keyframes lgt-spin{to{transform:rotate(360deg)}}

      /* ── Footer ──────────────────────────────────────────────────── */
      #lg-order-tracker .lgt-footer-note{text-align:center !important;font-size:12px !important;color:rgb(var(--color-foreground-rgb) / 0.4) !important;margin-top:24px !important;display:block !important}
      #lg-order-tracker .lgt-link{color:${c} !important;text-decoration:none !important}
      #lg-order-tracker .lgt-link:hover{text-decoration:underline !important}

      /* ── Mobile ────────────────────────────────────────────────── */
      @media(max-width:680px){
        #lg-order-tracker .lgt-body{grid-template-columns:1fr !important}
        #lg-order-tracker .lgt-sidebar{display:contents !important}
        #lg-order-tracker .lgt-hero{flex-direction:column !important;gap:10px !important}
        #lg-order-tracker .lgt-hero-right{text-align:left !important}
        #lg-order-tracker .lgt-hero-msg{font-size:18px !important}
        #lg-order-tracker .lgt-hero-search{padding:28px 20px !important}
        #lg-order-tracker .lgt-page-title{font-size:22px !important}
        #lg-order-tracker .lgt-seg-lbl{font-size:8.5px !important}
        /* Review + Help cards: match full width of sibling cards on mobile */
        #lg-order-tracker .lgt-review-card{max-width:100% !important}
        #lg-order-tracker .lgt-help-card{max-width:100% !important}
      }
    `;

    const style = document.createElement("style");
    style.id = "lgt-styles";
    style.textContent = css;
    document.head.appendChild(style);
  }
})();